import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Key, Zap, Droplet, Wind, Square, Shield, Clock, CheckCircle, Phone, AlertCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const ServicePage = () => {
  const { kategorie } = useParams();

  const serviceData = {
    'schluesseldienst': {
      icon: Key,
      title: 'Schlüsseldienst & Türöffnung',
      metaTitle: 'Schlüsseldienst NRW | 24/7 Türöffnung ohne Schäden',
      metaDesc: '24/7 Schlüsseldienst: Professionelle Türöffnung ohne Schäden. Schnelle Hilfe bei Aussperrung ☎ 0231 39083687. Faire Abwicklung im gesamten Ruhrgebiet.',
      description: 'Professionelle Türöffnung ohne Schäden – rund um die Uhr für Sie da',
      content: (
        <div className="space-y-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Ausgesperrt? Schlüssel verloren oder abgebrochen? Unser Schlüsseldienst-Team ist 24 Stunden am Tag, 7 Tage die Woche für Sie im Einsatz. 
            Wir öffnen Türen fachgerecht und in den meisten Fällen beschädigungsfrei. Mit modernsten Werkzeugen und jahrelanger Erfahrung 
            sind wir Ihr zuverlässiger Partner in Notlagen.
          </p>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Unsere Leistungen im Überblick</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Türöffnung bei verlorenen oder vergessenen Schlüsseln</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Schlüssel im Schloss abgebrochen – fachgerechte Extraktion</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Zylinderwechsel und Installation neuer Schließsysteme</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Sicherheitsberatung und Einbruchschutzmaßnahmen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Notfall-Schlüsseldienst bei Einbrüchen</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">So läuft unser Einsatz ab</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-[#e8f4f8] p-6 rounded-xl">
                <div className="text-3xl font-bold text-[#0066cc] mb-2">1</div>
                <h4 className="font-semibold text-gray-900 mb-2">Anruf</h4>
                <p className="text-gray-700">Sie rufen uns an und schildern Ihre Situation. Wir beraten Sie kostenlos.</p>
              </div>
              <div className="bg-[#e8f4f8] p-6 rounded-xl">
                <div className="text-3xl font-bold text-[#0066cc] mb-2">2</div>
                <h4 className="font-semibold text-gray-900 mb-2">Anfahrt</h4>
                <p className="text-gray-700">Unser Techniker macht sich schnellstmöglich auf den Weg zu Ihnen.</p>
              </div>
              <div className="bg-[#e8f4f8] p-6 rounded-xl">
                <div className="text-3xl font-bold text-[#0066cc] mb-2">3</div>
                <h4 className="font-semibold text-gray-900 mb-2">Lösung</h4>
                <p className="text-gray-700">Die Tür wird fachgerecht geöffnet – in den meisten Fällen ohne Beschädigung.</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Warum Handwerkernotdienst NRW?</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Shield className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Beschädigungsfreie Öffnung</p>
                  <p className="text-gray-700">Wir arbeiten mit modernstem Werkzeug und schonen Tür und Schloss.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Schnelle Reaktionszeit</p>
                  <p className="text-gray-700">Wir bemühen uns, innerhalb von 30-60 Minuten bei Ihnen zu sein.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Transparente Abläufe</p>
                  <p className="text-gray-700">Keine versteckten Kosten – Sie wissen vorher, was Sie erwartet.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      faqs: [
        { q: 'Wie lange dauert eine Türöffnung?', a: 'In den meisten Fällen dauert die Öffnung nur wenige Minuten. Die genaue Dauer hängt von der Art des Schlosses und der Tür ab.' },
        { q: 'Wird die Tür beschädigt?', a: 'Wir arbeiten professionell und mit geeignetem Werkzeug. In über 90% der Fälle können wir Türen ohne Beschädigung öffnen.' },
        { q: 'Was soll ich tun, wenn ich ausgesperrt bin?', a: 'Bleiben Sie ruhig und rufen Sie uns unter 0231 39083687 an. Wir beraten Sie am Telefon und schicken schnellstmöglich einen Techniker.' },
      ],
    },
    'elektro-notdienst': {
      icon: Zap,
      title: 'Elektro-Notdienst',
      metaTitle: 'Elektro-Notdienst NRW | 24/7 Hilfe bei Stromausfall',
      metaDesc: '24/7 Elektro-Notdienst: Schnelle Hilfe bei Stromausfall, defekten Sicherungen & elektrischen Notfällen ☎ 0231 39083687. Qualifizierte Elektriker im Ruhrgebiet.',
      description: 'Schnelle Hilfe bei Stromausfällen und elektrischen Notfällen',
      content: (
        <div className="space-y-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Stromausfall, defekte Sicherung oder ein elektrischer Notfall? Unser Elektro-Notdienst ist rund um die Uhr für Sie im Einsatz. 
            Qualifizierte Elektriker beheben das Problem schnell und sicher, damit Sie wieder unbesorgt Ihre elektrischen Geräte nutzen können.
          </p>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Unsere Elektro-Leistungen</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Behebung von Stromausfällen und Kurzschlüssen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Austausch defekter Sicherungen und FI-Schalter</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Reparatur von Steckdosen, Schaltern und Lampen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Fehlersuche in elektrischen Anlagen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Notfall-Maßnahmen bei elektrischen Gefahren</span>
              </li>
            </ul>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900 mb-2">Sicherheitshinweis</h4>
                <p className="text-red-800">
                  Bei elektrischen Notfällen kann Lebensgefahr bestehen. Führen Sie keine eigenen Reparaturversuche durch. 
                  Rufen Sie sofort unseren Elektro-Notdienst oder bei unmittelbarer Gefahr die Feuerwehr (112).
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      faqs: [
        { q: 'Was tun bei plötzlichem Stromausfall?', a: 'Prüfen Sie zunächst, ob nur Ihre Wohnung oder das ganze Haus betroffen ist. Kontrollieren Sie den Sicherungskasten. Wenn Sie die Ursache nicht finden oder unsicher sind, rufen Sie uns an.' },
        { q: 'Sind Ihre Elektriker qualifiziert?', a: 'Ja, alle unsere Elektriker verfügen über die erforderlichen Qualifikationen und Zulassungen für Elektroarbeiten.' },
        { q: 'Können Sie auch nachts kommen?', a: 'Selbstverständlich. Unser Elektro-Notdienst ist 24/7 verfügbar, auch nachts, an Wochenenden und Feiertagen.' },
      ],
    },
    'sanitaer-heizung': {
      icon: Droplet,
      title: 'Sanitär & Heizungsnotdienst',
      metaTitle: 'Sanitär & Heizungsnotdienst NRW | 24/7 bei Wasserschaden',
      metaDesc: '24/7 Sanitär-Notdienst: Soforthilfe bei Rohrbruch, Wasserschaden & Heizungsausfall ☎ 0231 39083687. Schnelle Schadensbegrenzung im Ruhrgebiet.',
      description: 'Sofortmaßnahmen bei Wasserschaden und Heizungsausfall',
      content: (
        <div className="space-y-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Rohrbruch, Wasserschaden oder Heizungsausfall im Winter? Unser Sanitär- und Heizungsnotdienst reagiert schnell und minimiert Folgeschäden. 
            Mit professionellem Equipment und erfahrenen Fachkräften sind wir Ihr Partner in sanitären Notlagen.
          </p>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Sanitär- & Heizungsleistungen</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Notfall-Maßnahmen bei Rohrbruch und Wasserschaden</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Reparatur undichter Leitungen und Armaturen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Heizungsausfall beheben – auch im Winter</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Verstopfte Abflüsse und Toiletten befreien</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Warmwasser-Notversorgung</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 p-6 rounded-xl">
            <h4 className="font-semibold text-gray-900 mb-3">Wichtig bei Wasserschaden:</h4>
            <ol className="space-y-2 list-decimal list-inside text-gray-700">
              <li>Hauptwasserhahn sofort zudrehen (meist im Keller oder neben der Wasseruhr)</li>
              <li>Strom in betroffenen Räumen abschalten (Sicherungskasten)</li>
              <li>Wasser mit Handtüchern oder Eimern aufnehmen</li>
              <li>Unseren Notdienst unter 0231 39083687 anrufen</li>
              <li>Bei großen Schäden: Versicherung informieren</li>
            </ol>
          </div>
        </div>
      ),
      faqs: [
        { q: 'Wo finde ich den Hauptwasserhahn?', a: 'Der Hauptwasserhahn befindet sich meist im Keller, in der Nähe der Wasseruhr oder im Hausanschlussraum. In Wohnungen oft im Bad oder in der Küche unter dem Spülbecken.' },
        { q: 'Was kostet ein Notdienst-Einsatz?', a: 'Die Kosten hängen vom Aufwand und der Tageszeit ab. Wir beraten Sie am Telefon transparent über zu erwartende Kosten.' },
        { q: 'Kann die Versicherung die Kosten übernehmen?', a: 'In vielen Fällen übernimmt die Hausrat- oder Wohngebäudeversicherung Notdiensteinsätze. Wir stellen Ihnen alle nötigen Unterlagen aus.' },
      ],
    },
    'rohrreinigung': {
      icon: Wind,
      title: 'Rohrreinigung',
      metaTitle: 'Rohrreinigung NRW | 24/7 bei Verstopfung & Abflussverstopfung',
      metaDesc: 'Professionelle Rohrreinigung bei Verstopfungen in Küche, Bad & WC. 24/7 verfügbar ☎ 0231 39083687. Nachhaltige Beseitigung ohne aggressive Chemikalien.',
      description: 'Professionelle Beseitigung von Verstopfungen',
      content: (
        <div className="space-y-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Verstopfte Abflüsse, überlaufende Toiletten oder Rückstau? Unser Rohrreinigungsdienst beseitigt Verstopfungen schnell und nachhaltig. 
            Mit professioneller Ausrüstung wie Rohrreinigungsspiralen und Hochdruckreinigern lösen wir das Problem effektiv.
          </p>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Rohrreinigung-Services</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Beseitigung von Verstopfungen in Küche, Bad und WC</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Reinigung von Abwasserleitungen mit Spirale oder Hochdruck</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Beseitigung von Rückstau und Überschwemmungen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Kanalinspektion mit Kamera</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Präventive Wartung und Reinigung</span>
              </li>
            </ul>
          </div>
        </div>
      ),
      faqs: [
        { q: 'Wie entstehen Verstopfungen?', a: 'Häufige Ursachen sind Haare, Seifenreste, Fette, Speisereste oder Fremdkörper in den Rohren. Auch Wurzeleinwuchs kann Abwasserleitungen verstopfen.' },
        { q: 'Kann ich selbst etwas tun?', a: 'Bei leichten Verstopfungen können Sie es mit einer Saugglocke oder biologischen Rohrreinigern versuchen. Vermeiden Sie aggressive Chemikalien. Bei hartnäckigen Verstopfungen: Rufen Sie uns an.' },
        { q: 'Wie lange dauert eine Rohrreinigung?', a: 'Je nach Art und Ort der Verstopfung dauert die Reinigung zwischen 30 Minuten und 2 Stunden.' },
      ],
    },
    'glas-notdienst': {
      icon: Square,
      title: 'Glas-Notdienst',
      metaTitle: 'Glas-Notdienst NRW | 24/7 Notverglasung nach Einbruch',
      metaDesc: '24/7 Glas-Notdienst: Schnelle Notverglasung bei zerbrochenen Fenstern & Türen ☎ 0231 39083687. Sofortsicherung nach Einbruch im Ruhrgebiet.',
      description: 'Schnelle Notverglasung bei zerbrochenen Fenstern und Türen',
      content: (
        <div className="space-y-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Zerbrochene Fenster oder Glastüren stellen ein Sicherheitsrisiko dar. Unser Glas-Notdienst sichert Ihr Objekt schnellstmöglich 
            durch eine professionelle Notverglasung. Wir schützen Ihr Eigentum vor Einbrüchen und Witterungseinflüssen.
          </p>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Glas-Notdienst Leistungen</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Notverglasung von Fenstern und Türen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Sofortmaßnahmen nach Einbruch oder Vandalismus</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Austausch von Isolier- und Sicherheitsglas</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Sicherung von Schaufenstern und Vitrinen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Beseitigung von Glasscherben</span>
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">Wichtig nach einem Glasschaden:</h4>
                <ul className="space-y-1 text-yellow-800">
                  <li>• Bereich absperren und Scherben vorsichtig entfernen</li>
                  <li>• Öffnung provisorisch mit Pappe oder Folie abdecken</li>
                  <li>• Bei Einbruch: Polizei informieren</li>
                  <li>• Unseren Glas-Notdienst kontaktieren</li>
                  <li>• Fotos für Versicherung machen</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ),
      faqs: [
        { q: 'Wie schnell können Sie eine Notverglasung durchführen?', a: 'Wir bemühen uns, innerhalb von 30-60 Minuten bei Ihnen zu sein und führen die Notverglasung sofort durch.' },
        { q: 'Was ist eine Notverglasung?', a: 'Eine Notverglasung ist eine vorübergehende Sicherung der Öffnung, um Ihr Objekt vor Einbruch und Wetter zu schützen. Die dauerhafte Verglasung erfolgt später.' },
        { q: 'Zahlt die Versicherung den Glasschaden?', a: 'In der Regel übernimmt die Hausrat- oder Glasversicherung solche Schäden. Wir stellen Ihnen alle nötigen Unterlagen aus.' },
      ],
    },
    'einbruchschutz': {
      icon: Shield,
      title: 'Einbruchschutz & Notverglasung',
      metaTitle: 'Einbruchschutz NRW | Sicherheitsmaßnahmen & Beratung',
      metaDesc: 'Professioneller Einbruchschutz: Sofortmaßnahmen nach Einbruch, Installation von Sicherheitstechnik ☎ 0231 39083687. Mechanische Sicherung nach DIN-Norm.',
      description: 'Sicherheitsmaßnahmen nach Einbrüchen und präventiver Schutz',
      content: (
        <div className="space-y-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Nach einem Einbruch oder zur Prävention: Wir sichern Ihr Objekt mit professionellen Einbruchschutzmaßnahmen. 
            Von der Sofortsicherung bis zur Installation moderner Sicherheitstechnik – Ihr Schutz ist unsere Priorität.
          </p>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Einbruchschutz-Services</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Sofortmaßnahmen nach Einbruch</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Installation von Sicherheitsschlössern und Panzerriegeln</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Montage von Fenstersicherungen und Rollladenverriegelungen</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Sicherheitsberatung für Privat- und Gewerbekunden</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Türspione und Schutzbeschläge</span>
              </li>
            </ul>
          </div>
        </div>
      ),
      faqs: [
        { q: 'Welche Einbruchschutzmaßnahmen sind sinnvoll?', a: 'Wichtig sind sichere Schließsysteme, Fenster- und Türsicherungen sowie gute Beleuchtung. Wir beraten Sie individuell für Ihr Objekt.' },
        { q: 'Was tun nach einem Einbruch?', a: 'Polizei rufen (110), nichts anfassen (Spurensicherung), Schäden dokumentieren und unseren Notdienst für Sofortsicherung kontaktieren.' },
        { q: 'Gibt es Förderungen für Einbruchschutz?', a: 'Ja, die KfW bietet Förderprogramme für Einbruchschutzmaßnahmen an. Wir informieren Sie gerne.' },
      ],
    },
  };

  const service = serviceData[kategorie] || serviceData['schluesseldienst'];
  const Icon = service.icon;

  // JSON-LD Schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.title,
    "provider": {
      "@type": "HomeAndConstructionBusiness",
      "name": "Handwerkernotdienst NRW – Schössow Dienstleistungen UG (haftungsbeschränkt)",
      "telephone": "+49-231-39083687",
      "email": "info@handwerkernotdienst.nrw"
    },
    "areaServed": {
      "@type": "AdministrativeArea",
      "name": "Nordrhein-Westfalen"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+49-231-39083687",
        "availableLanguage": "de"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title={service.metaTitle || `${service.title} | 24/7 Handwerkernotdienst NRW`}
        description={service.metaDesc || `${service.description}. 24/7 verfügbar ☎ 0231 39083687. Professioneller Service im gesamten Einsatzgebiet.`}
        canonicalUrl={`https://handwerkernotdienst.nrw/leistungen/${kategorie}`}
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' },
          { name: 'Leistungen', url: 'https://handwerkernotdienst.nrw/leistungen' },
          { name: service.title, url: `https://handwerkernotdienst.nrw/leistungen/${kategorie}` }
        ]}
        schema={schema}
      />
      
      <Header />
      <StickyCallButton />

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#e8f4f8] to-white py-16" data-testid="service-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-[#0066cc] rounded-xl flex items-center justify-center">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid="service-title">{service.title}</h1>
              <p className="text-lg text-gray-600 mt-2">{service.description}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-[#0066cc]">Start</Link>
            <span>/</span>
            <span>Leistungen</span>
            <span>/</span>
            <span className="text-gray-900">{service.title}</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="service-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {service.content}

          {/* CTA Box */}
          <div className="mt-12 bg-[#0066cc] rounded-2xl p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Brauchen Sie jetzt Hilfe?</h3>
            <p className="text-lg mb-6 opacity-90">Unser Team ist 24/7 für Sie erreichbar</p>
            <a href="tel:+4923139083687" data-testid="service-cta-button">
              <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white text-xl px-10 py-6 rounded-full font-bold">
                <Phone className="w-6 h-6 mr-2" />
                Jetzt anrufen: 0231 39083687
              </Button>
            </a>
          </div>

          {/* FAQ */}
          {service.faqs && service.faqs.length > 0 && (
            <div className="mt-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Häufige Fragen</h2>
              <Accordion type="single" collapsible className="space-y-4">
                {service.faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="bg-gray-50 rounded-lg px-6 border border-gray-200"
                    data-testid={`service-faq-${index}`}
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#0066cc] py-4">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;